"use client";

import { safeParseNumber, VehicleFilter } from "@alba-cars/common-modules";
import { Loader2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import ScrollToTop from "@/components/common/ScrollToTop";
import FilterDropdown from "@/components/ui/FilterDropdown";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { getValueOrFirstOption } from "@/utils/utils";
import { useVehicleFiltersData } from "../_hooks/useVehicleFilterData";
import { fuelTypeOptions } from "./_constants";
import CollapsibleSection from "./CollapsibleSection"; // Import the CollapsibleSection component
import { useFilterContext } from "./context/FilterContext";
import { EMIFilter } from "./EMIFilter";
import MileageInput from "./MileageInput";
import PriceFilter from "./PriceFilter";
import StockNumberInput from "./StockNumberInput";
import { BodyTypeOption, MakeOption, ModelOption } from "./types";
import { YearFilter } from "./YearFilter";

const DesktopFilterSidebar: React.FC = () => {
  const isSmallerDevice = useMediaQuery("(max-width: 1023px)");
  const scrollRef = useRef<{ scrollToTop: () => void } | null>(null);
  const { resetQuery, getCurrentFilters, updateFilters, isStateUpdating } = useFilterContext();

  const [localFilters, setLocalFilters] = useState<VehicleFilter | null>(null);

  const currentFilter = useMemo(() => getCurrentFilters(), [getCurrentFilters]);

  const {
    makesOptions,
    modelsOptions,
    featuresOptions,
    bodyTypeOptions,
    isMakesOptionsLoading,
    isModelsOptionsLoading,
    isFeaturesOptionsLoading,
    isBodyTypeOptionsLoading,
    makesLookup,
    modelsLookup,
    bodyTypeLookup,
    featuresLookup,
    fuelTypeLookup,
    setMakes: setSelectedMakes,
  } = useVehicleFiltersData();

  const handleAutoScroll = () => {
    scrollRef.current?.scrollToTop();
  };

  const handleFilterChange = useCallback(
    (newFilterValues: Partial<VehicleFilter>, callback?: Function) => {
      const currentFilters = getCurrentFilters();
      const updatedFilter = new VehicleFilter();
      Object.assign(updatedFilter, currentFilters, newFilterValues);

      setLocalFilters(updatedFilter);
      updateFilters(updatedFilter);
      callback?.();
    },
    [getCurrentFilters, updateFilters],
  );

  const handleReset = useCallback(() => {
    setLocalFilters(null);
    resetQuery();
    setSelectedMakes(undefined);
  }, [resetQuery, setSelectedMakes]);

  useEffect(() => {
    setLocalFilters(currentFilter);
  }, [currentFilter]);

  const selectedFeatures = useMemo(() => {
    const features = (localFilters?.features ?? []) as string[];
    if (!features.length) return null;

    return features.map((featureId) => featuresLookup.get(featureId)).filter(Boolean);
  }, [localFilters, featuresLookup]);

  const selectedMakes = useMemo(() => {
    const makes = (localFilters?.make ?? []) as string[];
    if (!makes.length) return null;

    return makes.map((makeSlug) => makesLookup.get(makeSlug)).filter(Boolean);
  }, [localFilters, makesLookup]);

  const selectedModels = useMemo(() => {
    const models = (localFilters?.model ?? []) as string[];
    if (!models.length) return null;

    return models.map((modelSlug) => modelsLookup.get(modelSlug)).filter(Boolean);
  }, [localFilters, modelsLookup]);

  const selectedBodyTypes = useMemo(() => {
    const bodyTypes = (localFilters?.bodyType ?? []) as string[];
    if (!bodyTypes.length) return null;

    return bodyTypes.map((bodyTypeSlug) => bodyTypeLookup.get(bodyTypeSlug)).filter(Boolean);
  }, [localFilters, bodyTypeLookup]);

  if (isSmallerDevice) {
    return null;
  }

  return (
    <aside aria-label="Filter options" className="sticky top-0 z-10">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleAutoScroll();
        }}
      >
        <div className="no-scrollbar hidden h-screen overflow-y-auto rounded-2xl pt-4 lg:flex lg:flex-col lg:gap-2">
          <div className="rounded-2xl bg-white p-4" role="region" aria-labelledby="filters-heading">
            <div className="flex items-center justify-between pb-3">
              {" "}
              <span className="flex items-center gap-2">
                <h2 className="inter px-2 text-lg font-semibold">Filters</h2>
                {isStateUpdating && <Loader2 className="h-5 w-5 animate-spin text-primary" />}
              </span>
              <button
                onClick={handleReset}
                className="inter text-sm text-secondary-foreground transition-colors hover:cursor-pointer hover:text-primary"
              >
                Reset All
              </button>
            </div>

            <div>
              <FilterDropdown
                id="make-filter"
                options={makesOptions ?? []}
                isLoading={isMakesOptionsLoading}
                helperText="Make"
                onSelect={(options) =>
                  handleFilterChange({ make: options.map((option: MakeOption) => option.slug) }, () => {
                    setSelectedMakes(options.map((option: MakeOption) => option.value));
                  })
                }
                onClear={() =>
                  handleFilterChange({ make: undefined, model: undefined }, () => {
                    setSelectedMakes(undefined);
                  })
                }
                value={selectedMakes as any}
                isMulti
              />
            </div>
            <div className="mt-4">
              <FilterDropdown
                id="model-filter"
                options={modelsOptions ?? []}
                isLoading={isModelsOptionsLoading}
                // isDisabled={!currentFilter?.make}
                helperText="Model"
                onSelect={(options) => handleFilterChange({ model: options.map((option: ModelOption) => option.slug) })}
                onClear={() => handleFilterChange({ model: undefined })}
                value={selectedModels as any}
                customNoOptionsMessage={() => "Please select a make first "}
                isMulti
              />
            </div>
            <div className="mt-4">
              <FilterDropdown
                id="body-type-filter"
                options={bodyTypeOptions ?? []}
                isLoading={isBodyTypeOptionsLoading}
                helperText="Body Type"
                onSelect={(options) => {
                  handleFilterChange({ bodyType: options.map((option: BodyTypeOption) => option.slug) });
                }}
                onClear={() => handleFilterChange({ bodyType: undefined })}
                value={selectedBodyTypes as any}
                isMulti
              />
            </div>
          </div>

          <CollapsibleSection title="Price & EMI" isOpen={true}>
            <PriceFilter
              onFromPriceChange={(fromPrice) => {
                const fromPriceValue = (fromPrice as unknown as { value: number; label: string })?.value;
                handleFilterChange({
                  price: { ...(currentFilter?.price ?? {}), min: fromPriceValue },
                });
              }}
              onFromPriceClear={() => {
                handleFilterChange({
                  price: { ...(currentFilter?.price ?? {}), min: undefined },
                });
              }}
              onToPriceChange={(toPrice) => {
                const toPriceValue = (toPrice as unknown as { value: number; label: string })?.value;
                handleFilterChange({
                  price: { ...(currentFilter?.price ?? {}), max: toPriceValue },
                });
              }}
              onToPriceClear={() => {
                handleFilterChange({
                  price: { ...(currentFilter?.price ?? {}), max: undefined },
                });
              }}
              onReset={() => handleFilterChange({ price: undefined })}
              fromValue={
                currentFilter?.price?.min
                  ? { value: currentFilter.price.min, label: currentFilter.price.min.toString() }
                  : null
              }
              toValue={
                currentFilter?.price?.max
                  ? { value: currentFilter.price.max, label: currentFilter.price.max.toString() }
                  : null
              }
            />

            <div className="mt-4">
              <EMIFilter
                onFromEMIChange={(fromPrice) => {
                  handleFilterChange({
                    emi: { ...(currentFilter?.emi ?? {}), min: Math.round(fromPrice?.value ?? 0) },
                  });
                }}
                onFromEMIClear={() => {
                  handleFilterChange({
                    emi: { ...(currentFilter?.emi ?? {}), min: undefined },
                  });
                }}
                onToEMIChange={(toPrice) => {
                  handleFilterChange({
                    emi: { ...(currentFilter?.emi ?? {}), max: Math.round(toPrice?.value ?? 0) },
                  });
                }}
                onToEMIClear={() => {
                  handleFilterChange({
                    emi: { ...(currentFilter?.emi ?? {}), max: undefined },
                  });
                }}
                onReset={() => {
                  handleFilterChange({ emi: undefined });
                }}
                fromValue={
                  currentFilter?.emi?.min
                    ? {
                        value: currentFilter.emi.min,
                        label: currentFilter.emi.min.toString(),
                      }
                    : null
                }
                toValue={
                  currentFilter?.emi?.max
                    ? {
                        value: currentFilter.emi.max,
                        label: currentFilter.emi.max.toString(),
                      }
                    : null
                }
              />
            </div>
          </CollapsibleSection>

          <CollapsibleSection title="Year & Mileage">
            <YearFilter
              onFromYearChange={(fromYear) => {
                const fromYearValue = (fromYear as unknown as { value: number; label: string })?.value;
                handleFilterChange({
                  year: { ...(currentFilter?.year ?? {}), min: Math.round(fromYearValue) },
                });
              }}
              onFromYearClear={() => {
                handleFilterChange({
                  year: { ...(currentFilter?.year ?? {}), min: undefined },
                });
              }}
              onToYearChange={(toYear) => {
                const toYearValue = (toYear as unknown as { value: number; label: string })?.value;
                handleFilterChange({
                  year: { ...(currentFilter?.year ?? {}), max: Math.round(toYearValue) },
                });
              }}
              onToYearClear={() => {
                handleFilterChange({
                  year: { ...(currentFilter?.year ?? {}), max: undefined },
                });
              }}
              onReset={() => handleFilterChange({ year: undefined })}
              fromValue={
                currentFilter?.year?.min
                  ? { value: currentFilter.year.min, label: currentFilter.year.min.toString() }
                  : null
              }
              toValue={
                currentFilter?.year?.max
                  ? { value: currentFilter.year.max, label: currentFilter.year.max.toString() }
                  : null
              }
            />

            <div className="mt-6">
              <MileageInput
                onFromMileageChange={(value) => {
                  handleFilterChange({
                    mileage: { ...(currentFilter?.mileage ?? {}), min: safeParseNumber(value) },
                  });
                }}
                onToMileageChange={(value) => {
                  handleFilterChange({
                    mileage: { ...(currentFilter?.mileage ?? {}), max: safeParseNumber(value) },
                  });
                }}
                onFromMileageClear={() => {
                  handleFilterChange({
                    mileage: { ...(currentFilter?.mileage ?? {}), min: undefined },
                  });
                }}
                onToMileageClear={() => {
                  handleFilterChange({
                    mileage: { ...(currentFilter?.mileage ?? {}), max: undefined },
                  });
                }}
                onReset={() => handleFilterChange({ mileage: undefined })}
                fromValue={currentFilter?.mileage?.min ? currentFilter.mileage.min.toString() : null}
                toValue={currentFilter?.mileage?.max ? currentFilter.mileage.max.toString() : null}
              />
            </div>
          </CollapsibleSection>

          <CollapsibleSection title="Additional Features">
            <div>
              <FilterDropdown
                id="fuel-type-filter"
                options={fuelTypeOptions}
                helperText="Fuel Type"
                withLabel={true}
                onSelect={(option) => handleFilterChange({ fuelType: option.value })}
                onClear={() => handleFilterChange({ fuelType: undefined })}
                value={
                  currentFilter?.fuelType
                    ? (fuelTypeLookup.get(getValueOrFirstOption(currentFilter.fuelType)) ?? null)
                    : null
                }
              />
            </div>
            <div className="mt-6">
              <FilterDropdown
                id="features-filter"
                options={featuresOptions ?? []}
                isLoading={isFeaturesOptionsLoading}
                helperText="Features"
                withLabel={true}
                menuPlacement="top"
                onSelect={(options) => {
                  const selectedOptions: { value: string; label: string }[] = options;
                  handleFilterChange({
                    features: selectedOptions.map((option) => option.value),
                  });
                }}
                onClear={() => handleFilterChange({ features: undefined })}
                value={selectedFeatures as any}
                isMulti
              />
            </div>
            <div className="mt-6">
              <StockNumberInput
                onValueChange={(value) => {
                  handleFilterChange({ chassisNumber: value });
                }}
                onReset={() => handleFilterChange({ chassisNumber: undefined })}
                value={
                  Array.isArray(currentFilter?.chassisNumber)
                    ? currentFilter?.chassisNumber[0]
                    : currentFilter?.chassisNumber
                }
                isLoading={isStateUpdating}
              />
            </div>
          </CollapsibleSection>

          <ScrollToTop ref={scrollRef} />
        </div>
      </form>
    </aside>
  );
};

export default DesktopFilterSidebar;
